import React, { useState } from 'react';
import { Search, Filter, Plus, Grid3x3 as Grid3X3, List, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Can } from '../../contexts/AbilityContext';
import PageTitle from '../../components/shared/PageTitle';
import subscriptionsData from '../../data/subscriptions.json';
import TeamMemberCard from '../../components/teams/TeamMemberCard';
import { useNavigate } from 'react-router-dom';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';

type TeamMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  phone?: string | null;
  assignedProspects: string[];
  stats: {
    totalProspects: number;
    activeProspects: number;
    convertedProspects: number;
    totalMeetings: number;
  };
  joinedDate: string;
  status: string;
};

type Role = {
  id: string;
  name: string;
  labelKey: string;
  color: string;
  permissions: string[];
  description: string;
};

const TeamsSales: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Fetch team members from API
  const { data: teamMembersData, isLoading, isError, error, refetch } = useGetTeamMembersQuery();

  const teamMembers: TeamMember[] = teamMembersData?.map(member => ({
    id: member.id,
    name: member.name,
    email: member.email,
    role: member.role,
    avatar: member.avatar,
    phone: member.phone ?? null,
    assignedProspects: member.assignedProspects,
    stats: {
      totalProspects: member.stats?.totalProspects || 0,
      activeProspects: member.stats?.activeProspects || 0,
      convertedProspects: member.stats?.convertedProspects || 0,
      totalMeetings: member.stats?.totalMeetings || 0,
    },
    joinedDate: member.joinedDate,
    status: member.status,
  })) || [];

  const roles: Role[] = [
    {
      id: 'marketing-manager',
      name: 'Marketing Manager',
      labelKey: 'teams.roles.marketingManager',
      color: 'amber',
      permissions: ['manage_team', 'edit_public_page', 'create_reward', 'publish_testimonial'],
      description: 'Manages marketing, public page, and team'
    },
    {
      id: 'manager',
      name: 'Manager',
      labelKey: 'teams.roles.manager',
      color: 'blue',
      permissions: ['assign_licenses', 'view_team_activity', 'assign_promoters_to_csm'],
      description: 'Full visibility, assigns promoters to CSM'
    },
    {
      id: 'account-manager',
      name: 'Account Manager',
      labelKey: 'teams.roles.accountManager',
      color: 'indigo',
      permissions: ['manage_accounts', 'view_prospects', 'edit_assigned_prospects', 'schedule_meetings'],
      description: 'Manages customer accounts and relationships'
    },
    {
      id: 'customer-success',
      name: 'Customer Success',
      labelKey: 'teams.roles.customerSuccess',
      color: 'green',
      permissions: ['view_prospects', 'edit_assigned_prospects', 'schedule_meetings', 'manage_support'],
      description: 'Ensures customer satisfaction and success'
    },
    {
      id: 'account-executive',
      name: 'Account Executive',
      labelKey: 'teams.roles.accountExecutive',
      color: 'orange',
      permissions: ['view_prospects', 'edit_assigned_prospects', 'schedule_meetings', 'create_deals'],
      description: 'Drives sales and closes deals'
    }
  ];

  const currentSubscription = subscriptionsData.products.find(
    p => p.id === subscriptionsData.currentSubscription.productId
  );

  const getLicenseLimit = () => {
    if (!currentSubscription) return 0;
    if (currentSubscription.licenseLimit === 'unlimited' || currentSubscription.licenseLimit === 'unlimited teams sales') {
      return Infinity;
    }
    const match = currentSubscription.licenseLimit.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  const licenseLimit = getLicenseLimit();
  const usedLicenses = teamMembers.length;
  const remainingLicenses = licenseLimit === Infinity ? Infinity : licenseLimit - usedLicenses;
  const isLimitReached = licenseLimit !== Infinity && usedLicenses >= licenseLimit;

  const getRoleColor = (role: string) => {
    const roleObj = roles.find(r => r.name === role || r.id === role);
    return roleObj?.color || 'gray';
  };

  const getRoleColorClasses = (color: string) => {
    const colorMap: { [key: string]: { badge: string; avatar: string } } = {
      amber: {
        badge: 'bg-amber-100 text-amber-700 border-amber-300',
        avatar: 'from-amber-500 to-amber-700'
      },
      indigo: {
        badge: 'bg-indigo-100 text-indigo-700 border-indigo-300',
        avatar: 'from-indigo-500 to-indigo-700'
      },
      purple: {
        badge: 'bg-purple-100 text-purple-700 border-purple-300',
        avatar: 'from-purple-500 to-purple-700'
      },
      blue: {
        badge: 'bg-blue-100 text-blue-700 border-blue-300',
        avatar: 'from-blue-500 to-blue-700'
      },
      green: {
        badge: 'bg-green-100 text-green-700 border-green-300',
        avatar: 'from-green-500 to-green-700'
      },
      orange: {
        badge: 'bg-orange-100 text-orange-700 border-orange-300',
        avatar: 'from-orange-500 to-orange-700'
      },
      pink: {
        badge: 'bg-pink-100 text-pink-700 border-pink-300',
        avatar: 'from-pink-500 to-pink-700'
      },
    };
    return colorMap[color] || {
      badge: 'bg-gray-100 text-gray-700 border-gray-300',
      avatar: 'from-gray-500 to-gray-700'
    };
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || member.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const handleViewDetails = (memberId: string) => {
    navigate(`/team-member/${memberId}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-[#4339A0]" />
          <p className="text-gray-600">{t('teams.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">{t('teams.loadError')}</h3>
              <p className="text-sm text-red-700 mb-4">
                {(error as any)?.data?.message || t('teams.loadErrorBody')}
              </p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('teams.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle title={t('teams.pageTitle')} />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('teams.heading')}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="bg-white rounded-lg shadow-sm px-4 py-2 border border-gray-200">
            <div className="flex items-center space-x-3">
              <div>
                <p className="text-xs text-gray-600">{t('teams.licenseUsage')}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-lg font-bold text-gray-900">{usedLicenses}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-lg font-semibold text-gray-600">
                    {licenseLimit === Infinity ? '∞' : licenseLimit}
                  </span>
                </div>
              </div>
              <button
                onClick={() => navigate('/my-subscription')}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                {t('teams.upgrade')}
              </button>
            </div>
          </div>
          <div className="flex items-center rounded-lg p-1" style={{ backgroundColor: '#E4E7EB' }}>
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              <span>{t('teams.grid')}</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span>{t('teams.listView')}</span>
            </button>
          </div>
          <Can I="manage" a="TeamMember">
            <button
              onClick={() => navigate('/add-team-member')}
              className={`btn-primary flex items-center space-x-2 ${
                isLimitReached ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isLimitReached}
              title={isLimitReached ? t('teams.limitReachedTooltip') : ''}
            >
              <Plus className="w-5 h-5" />
              <span>{t('teams.addMember')}</span>
            </button>
          </Can>
        </div>
      </div>

      {isLimitReached && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">{t('teams.limitReached')}</p>
            <p className="text-sm text-red-700 mt-1">
              {t('teams.limitReachedBody', { limit: licenseLimit })}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">{t('teams.section.heading')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('teams.section.subtitle')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('teams.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                >
                  <option value="all">{t('teams.allRoles')}</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>{t(role.labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {filteredMembers.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  roleColor={getRoleColor(member.role)}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMembers.map((member) => (
                <div key={member.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`w-12 h-12 bg-gradient-to-br ${getRoleColorClasses(getRoleColor(member.role)).avatar} rounded-xl flex items-center justify-center text-white font-bold shadow-md`}>
                        {member.avatar}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                          <span className={`px-3 py-1 rounded-lg text-xs font-semibold border ${getRoleColorClasses(getRoleColor(member.role)).badge}`}>
                            {member.role}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{member.email}</p>
                      </div>

                      <div className="hidden lg:flex items-center space-x-6">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">{t('teams.stats.totalProspects')}</p>
                          <p className="text-lg font-bold text-gray-900">{member.stats.totalProspects}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-emerald-600">{t('teams.stats.converted')}</p>
                          <p className="text-lg font-bold text-emerald-700">{member.stats.convertedProspects}</p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleViewDetails(member.id)}
                      className="ml-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {t('teams.view')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredMembers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('teams.empty')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamsSales;
